import { Col, Divider, InputNumber, InputNumberProps, Row, Slider, Switch, Typography } from 'antd';
import { Paper } from '../../../components/common/common';
import { SenseMode } from '../../../types/types';
import MeasurementParameters from '../../../components/widgets/Parameters/MeasurementParameters';
import TimingParameters from '../../../components/widgets/Parameters/TimingParameters';
import { useState } from 'react';

export interface PumpPowerSettings {
  setOnDevice: boolean;
  value: number;
}

type GlobalParametersComponentProps = {
  // Existing props
  showGlobalParameters: boolean;
  senseMode: SenseMode;
  pumpPower: PumpPowerSettings;
  setPumpPower: React.Dispatch<React.SetStateAction<PumpPowerSettings>>;
  autoStopPumpEnabled: boolean;
  setAutoStopPumpEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  autoStopPumpThreshold: number;
  setAutoStopPumpThreshold: React.Dispatch<React.SetStateAction<number>>;

  isGraphQlPushRunning: boolean;
  setIsGraphQlPushRunning: React.Dispatch<React.SetStateAction<boolean>>;

  // Auto-save measurement
  isAutoSavedMeasurementEnabled: boolean;
  setIsAutoSavedMeasurementEnabled: React.Dispatch<React.SetStateAction<boolean>>;

  // Humidity calibrant
  isRecordingCalibrantEnabled: boolean;
  setIsRecordingCalibrantEnabled: React.Dispatch<React.SetStateAction<boolean>>;

  // Durations (in seconds)
  baselineDuration: number; // default: 5 (handled in parent)
  setBaselineDuration: React.Dispatch<React.SetStateAction<number>>;

  analyteDuration: number; // default: 20 (handled in parent)
  setAnalyteDuration: React.Dispatch<React.SetStateAction<number>>;

  desorptionDuration: number; // default: 20 (handled in parent)
  setDesorptionDuration: React.Dispatch<React.SetStateAction<number>>;

  // Measurement metadata
  measurementName: string;
  setMeasurementName: React.Dispatch<React.SetStateAction<string>>;

  descriptionName: string;
  setDescriptionName: React.Dispatch<React.SetStateAction<string>>;
};

const GlobalParametersComponent: React.FC<GlobalParametersComponentProps> = ({
  showGlobalParameters,
  senseMode,

  pumpPower,
  setPumpPower,
  autoStopPumpEnabled,
  setAutoStopPumpEnabled,
  autoStopPumpThreshold,
  setAutoStopPumpThreshold,

  isGraphQlPushRunning,
  setIsGraphQlPushRunning,

  isAutoSavedMeasurementEnabled,
  setIsAutoSavedMeasurementEnabled,
  isRecordingCalibrantEnabled,
  setIsRecordingCalibrantEnabled,

  baselineDuration,
  setBaselineDuration,
  analyteDuration,
  setAnalyteDuration,
  desorptionDuration,
  setDesorptionDuration,

  measurementName,
  setMeasurementName,
  descriptionName,
  setDescriptionName,
}) => {
  const onPumpPowerSliderChange: InputNumberProps['onChange'] = (newValue) => {
    if (newValue === null) return;
    let x = newValue as number;
    setPumpPower({ setOnDevice: true, value: x });
  };

  const toggleSwitch = (checked: boolean) => {
    setIsGraphQlPushRunning(checked);
  };
  const [multiplicatorFactor, setMultiplicatorFactor] = useState<number>(1);

  return (
    <>
      {showGlobalParameters && (
        <Paper style={{ width: '100%', marginTop: 50, marginBottom: 30, padding: 24 }}>
          <Typography.Title level={5} style={{ textAlign: 'center', marginBottom: 30 }}>
            Global Parameters
          </Typography.Title>
          <MeasurementParameters
            senseMode={senseMode}
            isAutoSavedMeasurementEnabled={isAutoSavedMeasurementEnabled}
            setIsAutoSavedMeasurementEnabled={setIsAutoSavedMeasurementEnabled}
            isRecordingCalibrantEnabled={isRecordingCalibrantEnabled}
            setIsRecordingCalibrantEnabled={setIsRecordingCalibrantEnabled}
            measurementName={measurementName}
            setMeasurementName={setMeasurementName}
            descriptionName={descriptionName}
            setDescriptionName={setDescriptionName}
            multiplicatorFactor={multiplicatorFactor}
            setMultiplicatorFactor={setMultiplicatorFactor}
          />
          {/* ================= Timing Parameters ================= */}
          {senseMode === SenseMode.TimedRecording && (
            <TimingParameters
              baselineDuration={baselineDuration}
              setBaselineDuration={setBaselineDuration}
              analyteDuration={analyteDuration}
              setAnalyteDuration={setAnalyteDuration}
              desorptionDuration={desorptionDuration}
              setDesorptionDuration={setDesorptionDuration}
            />
          )}
          {/* ================= Hardware Parameters ================= */}
          <Typography.Title level={5}>Hardware Parameters</Typography.Title>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Typography.Text>Pump power (mW)</Typography.Text>
            </Col>
            <Col span={16}>
              <Slider min={0} max={1000} value={pumpPower.value} onChange={onPumpPowerSliderChange} />
            </Col>
            <Col span={8}>
              <InputNumber min={0} max={1000} style={{ width: '100%' }} value={pumpPower.value} onChange={onPumpPowerSliderChange} />
            </Col>
          </Row>
          <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Typography.Text>Auto stop pump</Typography.Text>
            </Col>
            <Col span={8}>
              <Switch checked={autoStopPumpEnabled} onChange={setAutoStopPumpEnabled} />
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Typography.Text>Auto stop pump threshold (rad)</Typography.Text>
            </Col>
            <Col span={4}>
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                value={autoStopPumpThreshold}
                onChange={(v) => {
                  if (v !== null) setAutoStopPumpThreshold(v);
                }}
              />
            </Col>
          </Row>
          <Divider />
          {/* ================= Data & Connectivity ================= */}
          <Typography.Title level={5}>Data & Connectivity</Typography.Title>
          <Row>
            <Col span={24}>
              <Typography.Text>GraphQL push</Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Switch checked={isGraphQlPushRunning} onChange={toggleSwitch} />
              </div>
            </Col>
          </Row>
        </Paper>
      )}
    </>
  );
};

export default GlobalParametersComponent;
